type ContentLane = {
  subject?: string | null;
  preheader?: string | null;
};

type MetadataLane = {
  subject?: string | null;
  preheader?: string | null;
};

/**
 * Overlay subject/preheader from the `message_contents_metadata` lane onto the
 * content row. Metadata is the source of truth for these two fields, with no
 * fallback to the content lane: a `null` is taken verbatim (cleared), and a
 * missing metadata row yields `null`
 */
export function mergeMessageContent<C extends ContentLane>(
  content: C | null | undefined,
  metadata: MetadataLane | null | undefined,
): C | null {
  if (!content) return null;
  return {
    ...content,
    subject: metadata?.subject ?? null,
    preheader: metadata?.preheader ?? null,
  };
}

type ProfileMapping = { templateKey?: string | null };

/**
 * Union the profile_mappings of body/block tags in message_contents,
 * subject/preheader tags in message_contents_metadata. mappings are deduped by templateKey
 * with any overlaps favoring the content's mappings.
 */
export function unionProfileMappings<M extends ProfileMapping>(
  content: M[] | null | undefined,
  metadata: M[] | null | undefined,
): M[] {
  const byKey = new Map<string, M>();
  for (const mapping of [...(content ?? []), ...(metadata ?? [])]) {
    const key = mapping?.templateKey;
    if (key && !byKey.has(key)) byKey.set(key, mapping);
  }
  return [...byKey.values()];
}

type MessageContentLanes<C> = {
  draft_version: number | null;
  draft_metadata_version: number | null;
  current_content: C | null | undefined;
  draft_content: C | null | undefined;
  current_metadata: MetadataLane | null | undefined;
  draft_metadata: MetadataLane | null | undefined;
};

/**
 * True when EITHER lane has a staged draft. The two draft pointers advance
 * independently, so gating on `draft_version` alone would miss metadata-only
 * drafts. `!= null` (not truthiness) — the versions are ints and `0` is valid.
 */
export function messageHasStagedChanges(message: {
  draft_version: number | null;
  draft_metadata_version: number | null;
}): boolean {
  return (
    message.draft_version != null || message.draft_metadata_version != null
  );
}

/**
 * Staged (editable-draft) view of a message's content, or null when neither
 * lane has a draft. Each lane falls back to its published row — the
 * `current_metadata` fallback is load-bearing: a body-only stage leaves the new
 * content row's subject/preheader null and would otherwise blank them.
 */
export function mergeStagedMessageContent<C extends ContentLane>(
  message: MessageContentLanes<C>,
): C | null {
  if (!messageHasStagedChanges(message)) return null;
  return mergeMessageContent(
    message.draft_content ?? message.current_content,
    message.draft_metadata ?? message.current_metadata,
  );
}
